// src/services/web3Service.js
import { ethers } from "ethers";
import Web3Modal from "web3modal";
import { 
  FUND_CONTRACT_ADDRESS, 
  FUND_ABI, 
  GOVERNANCE_CONTRACT_ADDRESS, 
  GOVERNANCE_ABI,
  YILDX_TOKEN_ADDRESS,
  YILDX_TOKEN_ABI
} from "../constants/contractInfo";

/**
 * Connects to wallet and initializes all contract instances with improved persistence
 */
export const connectWallet = async () => {
  // Initialize Web3Modal which manages the wallet connection UI
  const web3Modal = new Web3Modal({
    cacheProvider: true, // Important: enables provider caching for better UX
    providerOptions: {}, // Add your custom providers here
  });
  
  try {
    // Check if we've previously cached a provider
    let instance;
    
    if (web3Modal.cachedProvider) {
      console.log("Using cached provider:", web3Modal.cachedProvider);
      instance = await web3Modal.connectTo(web3Modal.cachedProvider);
    } else {
      // Connect with user interaction
      console.log("No cached provider, prompting user to connect...");
      instance = await web3Modal.connect();
    }
    
    // Initialize ethers provider with the connected instance
    const provider = new ethers.providers.Web3Provider(instance);
    const signer = provider.getSigner();
    
    // Initialize all contracts
    const fundContract = new ethers.Contract(FUND_CONTRACT_ADDRESS, FUND_ABI, signer);
    const governanceContract = new ethers.Contract(GOVERNANCE_CONTRACT_ADDRESS, GOVERNANCE_ABI, signer);
    const yildxTokenContract = new ethers.Contract(YILDX_TOKEN_ADDRESS, YILDX_TOKEN_ABI, signer);
    
    const account = await signer.getAddress();
    
    // Store account in localStorage for persistence across pages
    localStorage.setItem('connected_account', account);
    
    console.log("Successfully connected and initialized contracts for account:", account);
    
    // Setup event listeners for account and chain changes
    if (window.ethereum) {
      // We need to clean up any existing listeners first to avoid duplication
      window.ethereum.removeAllListeners('accountsChanged');
      window.ethereum.removeAllListeners('chainChanged');
      
      window.ethereum.on('accountsChanged', (newAccounts) => {
        if (newAccounts.length === 0) {
          // User disconnected their wallet
          console.log("Wallet disconnected");
          localStorage.removeItem('connected_account');
          // Don't use alert - log to console and let the UI handle it
          console.log("User disconnected wallet");
          // Application will automatically update UI based on connection state
        } else if (newAccounts[0].toLowerCase() !== account.toLowerCase()) {
          // Account changed
          console.log("Account changed from", account, "to", newAccounts[0]);
          localStorage.setItem('connected_account', newAccounts[0]);
          // Don't use confirm - reload automatically for consistency
          console.log("Account changed - reloading page for consistency");
          window.location.reload();
        }
      });
      
      window.ethereum.on('chainChanged', (chainId) => {
        console.log("Network changed to chainId:", chainId);
        // Don't use confirm - reload automatically which is the recommended approach
        console.log("Network changed - reloading page as recommended by MetaMask");
        window.location.reload();
      });
    }

    return { 
      provider, 
      signer, 
      account, 
      fundContract, 
      governanceContract, 
      yildxTokenContract,
      // For backward compatibility
      contract: fundContract 
    };
  } catch (error) {
    console.error("Error connecting wallet:", error);
    throw error;
  }
};

/**
 * Disconnects wallet and clears cached provider
 */
export const disconnectWallet = async () => {
  try {
    const web3Modal = new Web3Modal();
    
    // Clear the cached provider if it exists
    if (web3Modal.cachedProvider) {
      web3Modal.clearCachedProvider();
    }
    
    // Remove from localStorage
    localStorage.removeItem('connected_account');
    
    console.log("Wallet disconnected successfully");
    return true;
  } catch (error) {
    console.error("Error disconnecting wallet:", error);
    return false;
  }
};

/**
 * Gets ETH balance for a signer
 */
export const getEthBalance = async (signer) => {
  if (!signer) return "0";
  const balance = await signer.getBalance();
  return ethers.utils.formatEther(balance);
};

/**
 * Fetches NAV from contract
 */
export const fetchNav = async (contract) => {
  if (!contract) return "0";
  try {
    const navValue = await contract.calculateNAV();
    return ethers.utils.formatEther(navValue);
  } catch (error) {
    console.error("Error fetching NAV:", error);
    return "0";
  }
};

/**
 * Fetches ETH balance in fund for a user
 */
export const fetchFundEthBalance = async (contract, account) => {
  if (!contract || !account) return "0";
  try {
    const fundBalance = await contract.userDeposits(account);
    return ethers.utils.formatEther(fundBalance);
  } catch (error) {
    console.error("Error fetching fund ETH balance:", error);
    return "0";
  }
};

/**
 * Fetches fund token balance for a user
 */
export const fetchFundTokenBalance = async (contract, account) => {
  if (!contract || !account) return "0";
  try {
    const tokenBalance = await contract.userFundTokens(account);
    return ethers.utils.formatEther(tokenBalance);
  } catch (error) {
    console.error("Error fetching fund token balance:", error);
    return "0";
  }
};

/**
 * Fetches wallet token balance for a user
 */
export const fetchWalletTokenBalance = async (contract, account) => {
  if (!contract || !account) return "0";
  try {
    const tokenBalance = await contract.balanceOf(account);
    return ethers.utils.formatEther(tokenBalance);
  } catch (error) {
    console.error("Error fetching wallet token balance:", error);
    return "0";
  }
};

/**
 * Enhanced function to fetch the number of board seats a user has
 * using the new exposure functions
 */
export const fetchBoardSeats = async (yildxTokenContract, account) => {
  if (!yildxTokenContract || !account) {
    console.log("Cannot fetch board seats: Missing token contract or account");
    return 0;
  }
  
  try {
    console.log(`Fetching board seats for account: ${account}`);
    // Use the dedicated function to get board seats
    const seats = await yildxTokenContract.calculateSeats(account);
    const seatCount = seats.toNumber();
    
    console.log(`✅ Board seats found: ${seatCount}`);
    
    return seatCount;
  } catch (error) {
    console.error("Error fetching board seats:", error);
    
    // Try a different approach with calculateFractionalSeats
    try {
      console.log("Trying alternative method to get board seats...");
      const fractionalSeats = await yildxTokenContract.calculateFractionalSeats(account);
      // Convert from fractional (multiplied by 100) to whole seats
      const seatCount = Math.floor(fractionalSeats.toNumber() / 100);
      console.log(`✅ Board seats found (alt method): ${seatCount}`);
      return seatCount;
    } catch (altError) {
      console.error("Alternative method also failed:", altError);
      
      // Fall back to estimation method as last resort
      try {
        console.log("Falling back to estimation method...");
        const balance = await yildxTokenContract.balanceOf(account);
        const decimals = await yildxTokenContract.decimals();
        const formattedBalance = ethers.utils.formatUnits(balance, decimals);
        const numericBalance = parseFloat(formattedBalance);
        const TOKENS_PER_SEAT = 2000000; // 2 million tokens per seat
        const seats = Math.floor(numericBalance / TOKENS_PER_SEAT);
        console.log(`Estimated board seats for ${account}: ${seats} (based on token balance)`);
        return seats;
      } catch (finalError) {
        console.error("All seat determination methods failed:", finalError);
        return 0;
      }
    }
  }
};

/**
 * Fetches token sale information from the YILDX token contract
 */
export const fetchTokenSaleInfo = async (yildxTokenContract) => {
  if (!yildxTokenContract) return null;
  
  try {
    // These function calls will depend on your actual contract implementation
    const [
      totalSupply,
      tokensForSale,
      tokensSold,
      tokenPrice,
      minPurchase,
      maxPurchase
    ] = await Promise.all([
      yildxTokenContract.totalSupply().catch(e => ethers.BigNumber.from(0)),
      yildxTokenContract.tokensForSale().catch(e => ethers.BigNumber.from(0)),
      yildxTokenContract.tokensSold().catch(e => ethers.BigNumber.from(0)),
      yildxTokenContract.tokenPrice().catch(e => ethers.BigNumber.from(0)),
      yildxTokenContract.minPurchase().catch(e => ethers.BigNumber.from(0)),
      yildxTokenContract.maxPurchase().catch(e => ethers.BigNumber.from(0))
    ]);
    
    // Format values
    const decimals = await yildxTokenContract.decimals().catch(e => 18);
    
    return {
      totalSupply: ethers.utils.formatUnits(totalSupply, decimals),
      tokensForSale: ethers.utils.formatUnits(tokensForSale, decimals),
      tokensSold: ethers.utils.formatUnits(tokensSold, decimals),
      tokenPrice: ethers.utils.formatEther(tokenPrice),
      minPurchase: ethers.utils.formatUnits(minPurchase, decimals),
      maxPurchase: ethers.utils.formatUnits(maxPurchase, decimals)
    };
  } catch (error) {
    console.error("Error fetching token sale info:", error);
    return null;
  }
};

/**
 * Purchases YILDX tokens
 */
export const purchaseTokens = async (yildxTokenContract, amount, ethAmount) => {
  if (!yildxTokenContract) throw new Error("YILDX Token contract not initialized");
  
  // This will depend on your actual contract implementation
  return yildxTokenContract.purchaseTokens(amount, {
    value: ethers.utils.parseEther(ethAmount)
  });
};

/**
 * Deposits fund tokens back to the fund
 * @param {Object} contract - Fund contract instance
 * @param {Object} signer - Ethers signer
 * @param {String} amount - Amount of tokens to deposit as a string
 * @returns {Promise} Transaction object
 */
export const depositFundTokens = async (contract, signer, amount) => {
  if (!contract || !signer) {
    throw new Error("Contract or signer not available");
  }
  
  try {
    console.log(`Depositing ${amount} fund tokens back to the fund`);
    
    // Convert amount to proper format
    const parsedAmount = ethers.utils.parseEther(amount);
    
    // Check if the depositFundTokens function exists in the contract
    let hasDepositFunction = false;
    
    try {
      // Check if the function exists by examining the contract interface
      hasDepositFunction = Object.keys(contract.functions).some(
        fn => fn.startsWith("depositFundTokens(") || fn === "depositFundTokens"
      );
    } catch (err) {
      console.log("Error checking for depositFundTokens function:", err.message);
      hasDepositFunction = false;
    }
    
    if (hasDepositFunction) {
      // Call the depositFundTokens function if it exists
      console.log("Using depositFundTokens function");
      const tx = await contract.depositFundTokens(parsedAmount);
      return tx;
    } 
    
    // Check if there's a recoverTransferredTokens function
    let hasRecoverFunction = false;
    
    try {
      hasRecoverFunction = Object.keys(contract.functions).some(
        fn => fn.startsWith("recoverTransferredTokens(") || fn === "recoverTransferredTokens"
      );
    } catch (err) {
      console.log("Error checking for recoverTransferredTokens function:", err.message);
      hasRecoverFunction = false;
    }
    
    if (hasRecoverFunction) {
      console.log("Using recoverTransferredTokens function");
      
      // First transfer tokens to the fund
      const tx1 = await contract.transfer(contract.address, parsedAmount);
      console.log("Transfer transaction submitted:", tx1.hash);
      await tx1.wait();
      console.log("Transfer complete, recovering tokens");
      
      // Then recover them
      const tx2 = await contract.recoverTransferredTokens(parsedAmount);
      return tx2;
    }
    
    // If we can't find an appropriate function, don't proceed with the transaction
    throw new Error(
      "No safe deposit function found. The contract needs to have either depositFundTokens() " +
      "or recoverTransferredTokens() to safely track your tokens. Please contact the administrator."
    );
    
  } catch (error) {
    console.error("Error depositing fund tokens:", error);
    throw error;
  }
};

/**
 * Recovers tokens that were directly transferred to the fund
 * @param {Object} contract - Fund contract instance
 * @param {Object} signer - Ethers signer  
 * @param {String} amount - Amount of tokens to recover as a string
 * @returns {Promise} Transaction object
 */
export const recoverDirectlyTransferredTokens = async (contract, signer) => {
  if (!contract || !signer) {
    throw new Error("Contract or signer not available");
  }
  
  try {
    console.log("Checking for tokens that need recovery...");
    
    // Get the current address
    const account = await signer.getAddress();
    
    // Check how many tokens the contract has from this user
    let directBalance = await contract.balanceOf(account);
    console.log(`Direct token balance in wallet: ${ethers.utils.formatEther(directBalance)}`);
    
    // If there are no tokens to recover, inform the user
    if (directBalance.isZero()) {
      throw new Error(
        "No directly transferred tokens found to recover. You don't have any fund tokens in your wallet."
      );
    }
    
    // Check if the recoverTransferredTokens function exists
    let hasRecoverFunction = false;
    
    try {
      hasRecoverFunction = Object.keys(contract.functions).some(
        fn => fn.startsWith("recoverTransferredTokens(") || fn === "recoverTransferredTokens"
      );
    } catch (err) {
      console.log("Error checking for recoverTransferredTokens function:", err.message);
      hasRecoverFunction = false;
    }
    
    if (hasRecoverFunction) {
      console.log(`Found ${ethers.utils.formatEther(directBalance)} tokens to recover using recoverTransferredTokens`);
      
      // Use the recovery function
      const tx = await contract.recoverTransferredTokens(directBalance);
      return tx;
    }
    
    throw new Error(
      "No recovery function found. The contract needs to have recoverTransferredTokens() " +
      "to recover your tokens. Please contact the administrator."
    );
    
  } catch (error) {
    console.error("Error recovering tokens:", error);
    throw error;
  }
};

/**
 * Enhanced function to fetch YILDX token balance with detailed logging
 */
export const fetchYILDXTokenBalance = async (yildxTokenContract, account) => {
  if (!yildxTokenContract || !account) {
    console.log("Cannot fetch YILDX balance: Missing contract or account");
    return "0";
  }
  
  try {
    console.log(`Fetching YILDX token balance for account: ${account}`);
    const balance = await yildxTokenContract.balanceOf(account);
    const decimals = await yildxTokenContract.decimals();
    const formattedBalance = ethers.utils.formatUnits(balance, decimals);
    
    console.log(`✅ YILDX balance found: ${formattedBalance}`);
    
    return formattedBalance;
  } catch (error) {
    console.error("Error fetching YILDX token balance:", error);
    console.error("Error message:", error.message);
    return "0";
  }
};

/**
 * Fetches the pending fee amount a user can claim
 */
export const fetchPendingFees = async (yildxTokenContract, account) => {
  if (!yildxTokenContract || !account) return "0";
  try {
    const pendingFees = await yildxTokenContract.pendingFees(account);
    return ethers.utils.formatEther(pendingFees);
  } catch (error) {
    console.error("Error fetching pending fees:", error);
    return "0";
  }
};

/**
 * Claims fees for a user
 */
export const claimFees = async (yildxTokenContract) => {
  if (!yildxTokenContract) throw new Error("Token contract not initialized");
  return yildxTokenContract.claimFees();
};

/**
 * NEW FUNCTION: Redeems fund tokens from the governance token contract for ETH
 * This is used by the admin to redeem management fees
 */
export const redeemAdminFundTokens = async (fundContract, amount) => {
  if (!fundContract) throw new Error("Fund contract not initialized");
  
  console.log(`Redeeming ${ethers.utils.formatEther(amount)} fund tokens from governance contract...`);
  
  try {
    // Call the redeemAdminFundTokens function
    const tx = await fundContract.redeemAdminFundTokens(amount);
    console.log("Redemption transaction submitted:", tx.hash);
    
    // Wait for transaction to be mined
    const receipt = await tx.wait();
    console.log("Redemption transaction mined:", receipt);
    
    return tx;
  } catch (error) {
    console.error("Error redeeming admin fund tokens:", error);
    throw error;
  }
};

/**
 * NEW FUNCTION: Fetches fund tokens held by the governance token contract
 */
export const fetchFundTokensInGovernanceContract = async (fundContract, yildxTokenContract) => {
  if (!fundContract || !yildxTokenContract) return "0";
  
  try {
    console.log(`Checking fund tokens in governance contract...`);
    const tokenBalance = await fundContract.balanceOf(yildxTokenContract.address);
    const formattedBalance = ethers.utils.formatEther(tokenBalance);
    console.log(`Fund tokens in governance contract: ${formattedBalance}`);
    return formattedBalance;
  } catch (error) {
    console.error("Error fetching fund tokens in governance contract:", error);
    return "0";
  }
};

/**
 * Fetches all governance information for a user in a single call
 * using new board seat exposure functions
 */
export const fetchGovernanceInfo = async (yildxTokenContract, governanceContract, account) => {
  if (!yildxTokenContract || !account) {
    return {
      boardSeats: 0,
      yildxBalance: "0",
      pendingFees: "0",
      totalSeats: 0, // Will get actual value from contract
      votingPower: 0,
      hasGovernanceAccess: false,
      govTokenFundBalance: "0" // Default to 0
    };
  }
  
  try {
    console.log(`Fetching comprehensive governance info for: ${account}`);
    
    // Get all data in parallel for efficiency - using the new functions
    const [
      balance,
      seats,
      totalActiveSeats,
      decimals,
      pendingFeesBN,
      tokenName,
      tokenSymbol,
      // Add new check for fund tokens in the governance token contract
      fundTokensInGov
    ] = await Promise.all([
      yildxTokenContract.balanceOf(account).catch(e => ethers.BigNumber.from(0)),
      yildxTokenContract.calculateSeats(account).catch(e => ethers.BigNumber.from(0)),
      yildxTokenContract.getTotalActiveSeats().catch(e => ethers.BigNumber.from(0)),
      yildxTokenContract.decimals().catch(e => 18),
      yildxTokenContract.pendingFees(account).catch(e => ethers.BigNumber.from(0)),
      yildxTokenContract.name().catch(e => "YILDX Governance Token"),
      yildxTokenContract.symbol().catch(e => "YILDX"),
      // Add this to check for fund tokens in the governance token
      // If we have a fund contract address either from the governance contract or directly
      new Promise(async (resolve) => {
        try {
          // Try to get the fund contract from governance contract if available
          if (governanceContract && governanceContract.fundContract) {
            const fundAddr = await governanceContract.fundContract();
            const fundContract = new ethers.Contract(fundAddr, FUND_ABI, yildxTokenContract.provider);
            const balance = await fundContract.balanceOf(yildxTokenContract.address);
            resolve(balance);
          } else if (FUND_CONTRACT_ADDRESS) {
            // Otherwise use the direct fund address if we have it
            const fundContract = new ethers.Contract(FUND_CONTRACT_ADDRESS, FUND_ABI, yildxTokenContract.provider);
            const balance = await fundContract.balanceOf(yildxTokenContract.address);
            resolve(balance);
          } else {
            resolve(ethers.BigNumber.from(0));
          }
        } catch (e) {
          console.error("Error checking fund tokens in governance contract:", e);
          resolve(ethers.BigNumber.from(0));
        }
      })
    ]);
    
    // Format values
    const boardSeats = seats.toNumber ? seats.toNumber() : Number(seats.toString());
    const formattedBalance = ethers.utils.formatUnits(balance, decimals);
    const pendingFees = ethers.utils.formatEther(pendingFeesBN);
    const govTokenFundBalance = ethers.utils.formatEther(fundTokensInGov);
    
    // Get actual total seats from contract
    const totalSeats = totalActiveSeats.toNumber ? totalActiveSeats.toNumber() : Number(totalActiveSeats.toString());
    
    // Calculate voting power percentage (rounded to 2 decimals)
    const votingPower = totalSeats > 0 ? Math.round((boardSeats / totalSeats) * 10000) / 100 : 0;
    
    // Create a rich console log with all the information
    console.log(
      `%c Governance Info for ${account.substring(0, 8)}... %c\n` +
      `Token: ${tokenName} (${tokenSymbol})\n` +
      `Balance: ${formattedBalance} ${tokenSymbol}\n` +
      `Board Seats: ${boardSeats}\n` +
      `Voting Power: ${votingPower}%\n` +
      `Total Active Seats: ${totalSeats}\n` +
      `Pending Fees: ${pendingFees} ETH\n` +
      `Fund Tokens in Governance Contract: ${govTokenFundBalance}`,
      "background: #8e44ad; color: white; font-size: 14px; padding: 4px;", 
      "background: #f1f1f1; color: #333; font-size: 12px; padding: 8px; border: 1px solid #ddd;"
    );
    
    return {
      boardSeats,
      yildxBalance: formattedBalance,
      pendingFees,
      totalSeats,
      votingPower,
      tokenName,
      tokenSymbol,
      hasGovernanceAccess: boardSeats > 0,
      govTokenFundBalance  // Add this new field
    };
  } catch (error) {
    console.error("Error fetching governance info:", error);
    console.error("Error message:", error.message);
    
    // If we failed with the new method, try the legacy approach
    try {
      console.log("Falling back to legacy governance info fetch method");
      
      const [
        balance,
        decimals,
        pendingFeesBN,
        tokenName,
        tokenSymbol,
        // Add check for fund tokens in gov contract
        fundTokensInGov
      ] = await Promise.all([
        yildxTokenContract.balanceOf(account).catch(e => ethers.BigNumber.from(0)),
        yildxTokenContract.decimals().catch(e => 18),
        yildxTokenContract.pendingFees(account).catch(e => ethers.BigNumber.from(0)),
        yildxTokenContract.name().catch(e => "YILDX Governance Token"),
        yildxTokenContract.symbol().catch(e => "YILDX"),
        // Try to check for fund tokens
        (async () => {
          try {
            if (FUND_CONTRACT_ADDRESS) {
              const fundContract = new ethers.Contract(
                FUND_CONTRACT_ADDRESS,
                FUND_ABI,
                yildxTokenContract.provider
              );
              return await fundContract.balanceOf(yildxTokenContract.address);
            }
            return ethers.BigNumber.from(0);
          } catch (e) {
            return ethers.BigNumber.from(0);
          }
        })()
      ]);
      
      // Format values
      const formattedBalance = ethers.utils.formatUnits(balance, decimals);
      const pendingFees = ethers.utils.formatEther(pendingFeesBN);
      const govTokenFundBalance = ethers.utils.formatEther(fundTokensInGov);
      
      // Estimate board seats
      const numericBalance = parseFloat(formattedBalance);
      const TOKENS_PER_SEAT = 2000000; // 2 million tokens per seat
      const boardSeats = Math.floor(numericBalance / TOKENS_PER_SEAT);
      
      // Use fixed value for total seats based on contract design
      const totalSeats = 5;
      
      // Calculate voting power percentage (rounded to 2 decimals)
      const votingPower = totalSeats > 0 ? Math.round((boardSeats / totalSeats) * 10000) / 100 : 0;
      
      console.log("Using estimated governance info (legacy method)");
      
      return {
        boardSeats,
        yildxBalance: formattedBalance,
        pendingFees,
        totalSeats,
        votingPower,
        tokenName,
        tokenSymbol,
        hasGovernanceAccess: boardSeats > 0,
        govTokenFundBalance // Add fund token balance
      };
    } catch (fallbackError) {
      console.error("Fallback method also failed:", fallbackError);
      
      return {
        boardSeats: 0,
        yildxBalance: "0",
        pendingFees: "0",
        totalSeats: 5, // Default fallback
        votingPower: 0,
        hasGovernanceAccess: false,
        govTokenFundBalance: "0" // Default to 0
      };
    }
  }
};

/**
 * Gets board seat distribution from the contract
 */
export const fetchBoardSeatDistribution = async (yildxTokenContract) => {
  if (!yildxTokenContract) return null;
  
  try {
    console.log("Fetching board seat distribution...");
    const distribution = await yildxTokenContract.getBoardSeatDistribution();
    
    // Format the response
    const result = {
      holders: distribution.holders,
      seatCounts: distribution.seatCounts.map(s => s.toNumber()),
      percentages: distribution.percentages.map(p => p.toNumber())
    };
    
    console.log("✅ Board seat distribution:", result);
    return result;
  } catch (error) {
    console.error("Error fetching board seat distribution:", error);
    return null;
  }
};

/**
 * Gets all board seat holders with detailed information
 */
export const fetchAllBoardSeatHolders = async (yildxTokenContract) => {
  if (!yildxTokenContract) return [];
  
  try {
    console.log("Fetching all board seat holders...");
    const holders = await yildxTokenContract.getAllBoardSeatHolders();
    
    // Format the response
    const result = holders.map(h => ({
      address: h.holder,
      seats: h.seats.toNumber(),
      votingPower: h.votingPower.toNumber()
    }));
    
    console.log("✅ All board seat holders:", result);
    return result;
  } catch (error) {
    console.error("Error fetching board seat holders:", error);
    return [];
  }
};

/**
 * Exposes contract instances and helpful functions to window for debugging
 */
export const exposeContractsToWindow = (contracts) => {
  const { 
    provider, 
    signer, 
    account, 
    fundContract, 
    governanceContract, 
    yildxTokenContract 
  } = contracts;
  
  // Expose contracts to window
  window.fundContract = fundContract;
  window.governanceContract = governanceContract;
  window.yildxTokenContract = yildxTokenContract;
  window.provider = provider;
  window.signer = signer;
  window.ethers = ethers;
  
  // Expose helpful constants
  window.FUND_CONTRACT_ADDRESS = fundContract?.address;
  window.GOVERNANCE_CONTRACT_ADDRESS = governanceContract?.address;
  window.YILDX_TOKEN_ADDRESS = yildxTokenContract?.address;
  
  // Add helper functions
  window.checkBoardSeats = async (address = account) => {
    if (!yildxTokenContract) {
      console.error("YILDX token contract not available");
      return "0";
    }
    
    try {
      // Get board seats using the dedicated function
      const seats = await yildxTokenContract.calculateSeats(address);
      
      // Get total active seats for context
      const totalActiveSeats = await yildxTokenContract.getTotalActiveSeats();
      
      // Calculate voting power
      const votingPower = totalActiveSeats.gt(0) 
        ? seats.mul(100).div(totalActiveSeats).toNumber() 
        : 0;
      
      console.log(`Board seats for ${address}: ${seats.toString()} (${votingPower}% voting power)`);
      
      // Optionally try fractional seats too
      try {
        const fractionalSeats = await yildxTokenContract.calculateFractionalSeats(address);
        console.log(`Fractional seats: ${fractionalSeats.toNumber() / 100} (${fractionalSeats.toString()} raw value)`);
      } catch (err) {
        console.log("Fractional seats not available");
      }
      
      return seats.toString();
    } catch (err) {
      console.error("Error checking board seats:", err);
      
      // Fall back to estimation method
      try {
        const balance = await yildxTokenContract.balanceOf(address);
        const decimals = await yildxTokenContract.decimals();
        const formattedBalance = ethers.utils.formatUnits(balance, decimals);
        const numericBalance = parseFloat(formattedBalance);
        const TOKENS_PER_SEAT = 2000000; // 2 million tokens per seat
        const seats = Math.floor(numericBalance / TOKENS_PER_SEAT);
        console.log(`Estimated board seats for ${address}: ${seats} (based on token balance)`);
        return seats.toString();
      } catch (tokenErr) {
        console.error("Error estimating seats from token balance:", tokenErr);
        return "0";
      }
    }
  };
  
  window.checkYILDXBalance = async (address = account) => {
    if (!yildxTokenContract) {
      console.error("YILDX token contract not available");
      return null;
    }
    
    try {
      const balance = await yildxTokenContract.balanceOf(address);
      const decimals = await yildxTokenContract.decimals();
      const formattedBalance = ethers.utils.formatUnits(balance, decimals);
      console.log(`YILDX balance for ${address}: ${formattedBalance}`);
      return formattedBalance;
    } catch (err) {
      console.error("Error checking YILDX balance:", err);
      return null;
    }
  };
  
  window.checkGovernanceInfo = async (address = account) => {
    return fetchGovernanceInfo(yildxTokenContract, governanceContract, address);
  };
  
  window.checkGovernanceDistribution = async () => {
    if (!yildxTokenContract) {
      console.error("YILDX token contract not available");
      return null;
    }
    
    try {
      const distribution = await yildxTokenContract.getBoardSeatDistribution();
      console.log("Board seat distribution:", {
        holders: distribution.holders,
        seatCounts: distribution.seatCounts.map(s => s.toString()),
        percentages: distribution.percentages.map(p => p.toString() + "%")
      });
      
      return distribution;
    } catch (err) {
      console.error("Error checking governance distribution:", err);
      return null;
    }
  };
  
  window.getAllBoardSeatHolders = async () => {
    if (!yildxTokenContract) {
      console.error("YILDX token contract not available");
      return null;
    }
    
    try {
      const holders = await yildxTokenContract.getAllBoardSeatHolders();
      
      // Format for display
      const formattedHolders = holders.map(h => ({
        address: h.holder,
        seats: h.seats.toString(),
        votingPower: h.votingPower.toString() + "%"
      }));
      
      console.table(formattedHolders);
      return holders;
    } catch (err) {
      console.error("Error getting all board seat holders:", err);
      return null;
    }
  };
  
  // Add new function for checking fund tokens in governance contract
  window.checkFundTokensInGovernance = async () => {
    if (!fundContract || !yildxTokenContract) {
      console.error("Fund contract or YILDX token contract not available");
      return "0";
    }
    
    try {
      return await fetchFundTokensInGovernanceContract(fundContract, yildxTokenContract);
    } catch (err) {
      console.error("Error checking fund tokens in governance:", err);
      return "0";
    }
  };
  
  // Add new function for redeeming admin fund tokens
  window.redeemAdminFundTokens = async () => {
    if (!fundContract || !yildxTokenContract) {
      console.error("Fund contract or YILDX token contract not available");
      return false;
    }
    
    try {
      // Get fund tokens in governance contract
      const fundTokens = await fundContract.balanceOf(yildxTokenContract.address);
      
      if (fundTokens.gt(0)) {
        console.log(`Found ${ethers.utils.formatEther(fundTokens)} fund tokens in governance contract`);
        
        // Log the instructions instead of using confirm
        console.log(`To redeem ${ethers.utils.formatEther(fundTokens)} fund tokens, call 'await fundContract.redeemAdminFundTokens(...)' with the amount`);
        console.log("Example: await fundContract.redeemAdminFundTokens(ethers.utils.parseEther('X'))");
        
        // Just display information and return the amount
        return ethers.utils.formatEther(fundTokens);
      } else {
        console.log("No fund tokens found in governance contract");
        return false;
      }
    } catch (err) {
      console.error("Error checking fund tokens:", err);
      return false;
    }
  };
  
  // Add operator management helpers
  window.checkOperatorStatus = async (address = account) => {
    if (!fundContract) {
      console.error("Fund contract not available");
      return false;
    }
    
    try {
      return await isOperator(fundContract, address);
    } catch (err) {
      console.error("Error checking operator status:", err);
      return false;
    }
  };
  
  window.getAllOperators = async () => {
    if (!fundContract) {
      console.error("Fund contract not available");
      return [];
    }
    
    try {
      return await getAllOperators(fundContract);
    } catch (err) {
      console.error("Error getting all operators:", err);
      return [];
    }
  };
  
  window.getCurrentOperator = async () => {
    if (!fundContract) {
      console.error("Fund contract not available");
      return null;
    }
    
    try {
      return await getOperator(fundContract);
    } catch (err) {
      console.error("Error getting current operator:", err);
      return null;
    }
  };
  
  // Log available functions
  console.log("%c Web3 Debugging Tools Exposed %c", 
    "background: #2c3e50; color: white; font-size: 14px; padding: 4px;",
    "");
  console.log("Available in console: fundContract, governanceContract, yildxTokenContract");
  console.log("Helper functions: checkBoardSeats(), checkYILDXBalance(), checkGovernanceInfo(), checkGovernanceDistribution(), getAllBoardSeatHolders()");
  console.log("Fee functions: checkFundTokensInGovernance(), redeemAdminFundTokens()");
  console.log("Operator functions: checkOperatorStatus(), getAllOperators(), getCurrentOperator()");
  
  return window.checkGovernanceInfo();
};

/**
 * Fetches all active proposals
 */
export const fetchActiveProposals = async (governanceContract) => {
  if (!governanceContract) return [];
  try {
    // Get the proposal count
    const proposalCount = await governanceContract.proposalCount();
    
    // Fetch all proposals
    const proposals = [];
    for (let i = 0; i < proposalCount.toNumber(); i++) {
      const proposal = await governanceContract.getProposal(i);
      proposals.push({
        id: i,
        ...proposal
      });
    }
    
    return proposals;
  } catch (error) {
    console.error("Error fetching proposals:", error);
    return [];
  }
};

/**
 * Fetches a specific proposal
 */
export const fetchProposal = async (governanceContract, proposalId) => {
  if (!governanceContract) return null;
  try {
    const proposal = await governanceContract.getProposal(proposalId);
    return {
      id: proposalId,
      ...proposal
    };
  } catch (error) {
    console.error(`Error fetching proposal ${proposalId}:`, error);
    return null;
  }
};

/**
 * Checks if a user has voted on a proposal
 */
export const checkUserVote = async (governanceContract, proposalId, account) => {
  if (!governanceContract || !account) return { hasVoted: false, support: false, weight: 0 };
  try {
    const [hasVoted, support, weight] = await governanceContract.hasVoted(proposalId, account);
    return { hasVoted, support, weight: weight.toNumber() };
  } catch (error) {
    console.error(`Error checking vote for proposal ${proposalId}:`, error);
    return { hasVoted: false, support: false, weight: 0 };
  }
};

/**
 * Creates a new proposal
 */
export const createProposal = async (governanceContract, proposalType, description, callData) => {
  if (!governanceContract) throw new Error("Governance contract not initialized");
  return governanceContract.createProposal(proposalType, description, callData);
};

/**
 * Votes on a proposal
 */
export const castVote = async (governanceContract, proposalId, support) => {
  if (!governanceContract) throw new Error("Governance contract not initialized");
  return governanceContract.castVote(proposalId, support);
};

/**
 * Executes a proposal
 */
export const executeProposal = async (governanceContract, proposalId) => {
  if (!governanceContract) throw new Error("Governance contract not initialized");
  return governanceContract.executeProposal(proposalId);
};

/**
 * Cancels a proposal
 */
export const cancelProposal = async (governanceContract, proposalId) => {
  if (!governanceContract) throw new Error("Governance contract not initialized");
  return governanceContract.cancelProposal(proposalId);
};

/**
 * Checks a proposal's auto-approval status
 */
export const checkAutoApprovalStatus = async (governanceContract, proposalId) => {
  if (!governanceContract) throw new Error("Governance contract not initialized");
  try {
    const status = await governanceContract.checkAutoApprovalStatus(proposalId);
    return {
      canAutoApprove: status.canAutoApprove,
      requiredVotes: status.requiredVotes.toNumber(),
      currentVotes: status.currentVotes.toNumber(),
      threshold: status.threshold.toNumber()
    };
  } catch (error) {
    console.error(`Error checking auto-approval status for proposal ${proposalId}:`, error);
    return { canAutoApprove: false, requiredVotes: 0, currentVotes: 0, threshold: 0 };
  }
};

/**
 * Gets call data for various proposal types
 */
export const getAddTokenCallData = async (governanceContract, tokenAddress, allocation, priceFeed) => {
  if (!governanceContract) throw new Error("Governance contract not initialized");
  return governanceContract.getAddTokenCallData(tokenAddress, allocation, priceFeed);
};

export const getRemoveTokenCallData = async (governanceContract, tokenAddress) => {
  if (!governanceContract) throw new Error("Governance contract not initialized");
  return governanceContract.getRemoveTokenCallData(tokenAddress);
};

export const getUpdateTokenAllocationCallData = async (governanceContract, tokenAddress, newAllocation) => {
  if (!governanceContract) throw new Error("Governance contract not initialized");
  return governanceContract.getUpdateTokenAllocationCallData(tokenAddress, newAllocation);
};

export const getSetManagementFeeCallData = async (governanceContract, newFee) => {
  if (!governanceContract) throw new Error("Governance contract not initialized");
  return governanceContract.getSetManagementFeeCallData(newFee);
};

export const getSetEntryFeeCallData = async (governanceContract, newFee) => {
  if (!governanceContract) throw new Error("Governance contract not initialized");
  return governanceContract.getSetEntryFeeCallData(newFee);
};

export const getSetExitFeeCallData = async (governanceContract, newFee) => {
  if (!governanceContract) throw new Error("Governance contract not initialized");
  return governanceContract.getSetExitFeeCallData(newFee);
};

export const getSetSlippageToleranceCallData = async (governanceContract, newSlippage) => {
  if (!governanceContract) throw new Error("Governance contract not initialized");
  return governanceContract.getSetSlippageToleranceCallData(newSlippage);
};

/**
 * Delegates tokens to another address
 */
export const delegateTokens = async (governanceContract, delegateeAddress) => {
  if (!governanceContract) throw new Error("Governance contract not initialized");
  return governanceContract.delegateTokens(delegateeAddress);
};

/**
 * Removes existing delegation
 */
export const removeDelegation = async (governanceContract) => {
  if (!governanceContract) throw new Error("Governance contract not initialized");
  return governanceContract.removeDelegation();
};

/**
 * Gets current delegation information for a delegator
 */
export const getDelegationInfo = async (governanceContract, delegator) => {
  if (!governanceContract || !delegator) {
    console.error("Cannot get delegation info: Missing governance contract or delegator");
    return { delegatee: ethers.constants.AddressZero, hasDelegated: false };
  }
  
  try {
    console.log(`Getting delegation info for delegator: ${delegator}`);
    const delegatee = await governanceContract.delegations(delegator);
    
    return {
      delegatee,
      hasDelegated: delegatee !== ethers.constants.AddressZero
    };
  } catch (error) {
    console.error("Error getting delegation info:", error);
    return { delegatee: ethers.constants.AddressZero, hasDelegated: false };
  }
};

/**
 * Gets voting weight for an address (including delegated votes)
 */
export const getVotingWeight = async (governanceContract, address) => {
  if (!governanceContract || !address) {
    console.error("Cannot get voting weight: Missing governance contract or address");
    return "0";
  }
  
  try {
    console.log(`Getting voting weight for address: ${address}`);
    const weight = await governanceContract.getVotingWeight(address);
    return weight.toString();
  } catch (error) {
    console.error("Error getting voting weight:", error);
    return "0";
  }
};

/**
 * Gets all delegators who have delegated to a specific address
 */
export const getDelegatorsFor = async (governanceContract, delegatee) => {
  if (!governanceContract || !delegatee) {
    console.error("Cannot get delegators: Missing governance contract or delegatee");
    return [];
  }
  
  try {
    console.log(`Getting all delegators for delegatee: ${delegatee}`);
    
    // Get all DelegationChanged events
    const filter = governanceContract.filters.DelegationChanged(null, delegatee);
    const events = await governanceContract.queryFilter(filter);
    
    // Extract unique delegators who have delegated to this address
    const delegators = [];
    const seenDelegators = new Set();
    
    for (const event of events) {
      const { delegator } = event.args;
      
      if (!seenDelegators.has(delegator)) {
        seenDelegators.add(delegator);
        
        // Verify this is still the current delegation (hasn't been removed or changed)
        const currentDelegatee = await governanceContract.delegations(delegator);
        if (currentDelegatee.toLowerCase() === delegatee.toLowerCase()) {
          delegators.push(delegator);
        }
      }
    }
    
    return delegators;
  } catch (error) {
    console.error(`Error getting delegators for ${delegatee}:`, error);
    return [];
  }
};

/**
 * Gets all proposals a user has voted on
 */
export const getUserVotingHistory = async (governanceContract, account) => {
  if (!governanceContract || !account) {
    console.error("Cannot get voting history: Missing governance contract or account");
    return [];
  }
  
  try {
    console.log(`Getting voting history for account: ${account}`);
    
    // Get all VoteCast events for this account
    const filter = governanceContract.filters.VoteCast(account);
    const events = await governanceContract.queryFilter(filter);
    
    // Map events to voting history
    const votingHistory = [];
    
    for (const event of events) {
      const { proposalId, support, votes, timestamp } = event.args;
      
      // Get proposal details
      let proposalDetails;
      try {
        proposalDetails = await governanceContract.getProposal(proposalId);
      } catch (error) {
        console.warn(`Could not get details for proposal ${proposalId}:`, error.message);
        proposalDetails = { 
          description: 'Unknown proposal',
          executed: false,
          canceled: false,
          votingEndTime: 0
        };
      }
      
      votingHistory.push({
        proposalId: proposalId.toString(),
        description: proposalDetails.description,
        support,
        votes: votes.toString(),
        timestamp: timestamp.toString(),
        date: new Date(timestamp * 1000).toLocaleString(),
        executed: proposalDetails.executed,
        canceled: proposalDetails.canceled,
        votingEndTime: proposalDetails.votingEndTime.toString()
      });
    }
    
    // Sort by timestamp, newest first
    votingHistory.sort((a, b) => b.timestamp - a.timestamp);
    
    return votingHistory;
  } catch (error) {
    console.error(`Error getting voting history for ${account}:`, error);
    return [];
  }
};

/**
 * Gets total delegated voting power for an address
 */
export const getDelegatedVotingPower = async (governanceContract, delegatee) => {
  if (!governanceContract || !delegatee) {
    console.error("Cannot get delegated voting power: Missing governance contract or delegatee");
    return "0";
  }
  
  try {
    console.log(`Getting delegated voting power for delegatee: ${delegatee}`);
    
    // Get all delegators who have delegated to this address
    const delegators = await getDelegatorsFor(governanceContract, delegatee);
    
    if (delegators.length === 0) {
      return "0";
    }
    
    // Get the total voting power delegated
    let totalDelegatedPower = ethers.BigNumber.from(0);
    
    for (const delegator of delegators) {
      // Get delegator's voting power
      try {
        const voteBalance = await governanceContract.getVoteBalance(delegator);
        totalDelegatedPower = totalDelegatedPower.add(voteBalance);
      } catch (err) {
        console.warn(`Could not get vote balance for ${delegator}:`, err.message);
      }
    }
    
    return totalDelegatedPower.toString();
  } catch (error) {
    console.error(`Error getting delegated voting power for ${delegatee}:`, error);
    return "0";
  }
};

/**
 * Gets the operator of the fund
 */
export const getOperator = async (fundContract) => {
  if (!fundContract) return null;
  
  try {
    // Try multiple methods to get the operator
    // Method 1: getOperator function
    try {
      const operator = await fundContract.getOperator();
      if (operator && operator !== ethers.constants.AddressZero) {
        return operator;
      }
    } catch (error) {
      console.log("getOperator function not available:", error.message);
    }
    
    // Method 2: Try operators mapping at index 0
    try {
      const operator = await fundContract.operators(0);
      if (operator && operator !== ethers.constants.AddressZero) {
        return operator;
      }
    } catch (error) {
      console.log("operators mapping not available:", error.message);
    }
    
    // Method 3: Try to get operator from events
    try {
      const filter = fundContract.filters.OperatorAdded();
      const events = await fundContract.queryFilter(filter);
      
      if (events.length > 0) {
        // Get the most recent operator added
        const lastEvent = events[events.length - 1];
        return lastEvent.args.operator;
      }
    } catch (error) {
      console.log("Could not get operator from events:", error.message);
    }
    
    return null;
  } catch (error) {
    console.error("Error getting operator:", error);
    return null;
  }
};

/**
 * Gets calldata for adding an operator
 */
export const getAddOperatorCallData = async (governanceContract, operatorAddress) => {
  if (!governanceContract) throw new Error("Governance contract not initialized");
  
  try {
    // First try the dedicated function if available
    try {
      return await governanceContract.getAddOperatorCallData(operatorAddress);
    } catch (error) {
      console.log("getAddOperatorCallData not available, generating manually:", error.message);
      
      // Manually create calldata if the function doesn't exist
      // This assumes the fund contract has a function called "addOperator(address)"
      const functionSignature = "addOperator(address)";
      const functionSelector = ethers.utils.id(functionSignature).substring(0, 10);
      const encodedParams = ethers.utils.defaultAbiCoder.encode(["address"], [operatorAddress]);
      
      return functionSelector + encodedParams.substring(2); // Remove '0x' prefix from encoded params
    }
  } catch (error) {
    console.error("Error getting add operator calldata:", error);
    throw error;
  }
};

/**
 * Gets calldata for removing an operator
 */
export const getRemoveOperatorCallData = async (governanceContract, operatorAddress) => {
  if (!governanceContract) throw new Error("Governance contract not initialized");
  
  try {
    // First try the dedicated function if available
    try {
      return await governanceContract.getRemoveOperatorCallData(operatorAddress);
    } catch (error) {
      console.log("getRemoveOperatorCallData not available, generating manually:", error.message);
      
      // Manually create calldata if the function doesn't exist
      // This assumes the fund contract has a function called "removeOperator(address)"
      const functionSignature = "removeOperator(address)";
      const functionSelector = ethers.utils.id(functionSignature).substring(0, 10);
      const encodedParams = ethers.utils.defaultAbiCoder.encode(["address"], [operatorAddress]);
      
      return functionSelector + encodedParams.substring(2); // Remove '0x' prefix from encoded params
    }
  } catch (error) {
    console.error("Error getting remove operator calldata:", error);
    throw error;
  }
};

/**
 * Checks if an address is an operator
 */
export const isOperator = async (fundContract, address) => {
  if (!fundContract || !address) return false;
  
  try {
    // Try multiple methods to check if the address is an operator
    
    // Method 1: Use authorizedOperators function if available
    try {
      return await fundContract.authorizedOperators(address);
    } catch (error) {
      console.log("authorizedOperators function not available:", error.message);
    }
    
    // Method 2: Use isOperator function if available
    try {
      return await fundContract.isOperator(address);
    } catch (error) {
      console.log("isOperator function not available:", error.message);
    }
    
    // Method 3: Use operators mapping if available
    try {
      return await fundContract.operators(address);
    } catch (error) {
      console.log("operators mapping access not available:", error.message);
    }
    
    // If no method worked and we're in development mode, return true for a hardcoded operator
    if (process.env.NODE_ENV === 'development' && 
        address.toLowerCase() === '0x15d34AAf54267DB7D7c367839AAf71A00a2C6A65'.toLowerCase()) {
      console.log("Using hardcoded operator for development");
      return true;
    }
    
    return false;
  } catch (error) {
    console.error("Error checking operator status:", error);
    return false;
  }
};

/**
 * Gets all operators for the fund
 */
export const getAllOperators = async (fundContract) => {
  if (!fundContract) return [];
  
  try {
    // Try to get all operators using events
    try {
      const filter = fundContract.filters.OperatorAdded();
      const events = await fundContract.queryFilter(filter);
      
      if (events.length > 0) {
        // Get all operators from events
        const operators = events.map(event => event.args.operator);
        
        // Deduplicate operators
        const uniqueOperators = [...new Set(operators)];
        
        // Now filter out any operators that were removed
        const activeOperators = [];
        for (const operator of uniqueOperators) {
          // Check if this operator is still active
          const isActive = await isOperator(fundContract, operator);
          if (isActive) {
            activeOperators.push(operator);
          }
        }
        
        return activeOperators;
      }
    } catch (error) {
      console.log("Could not get operators from events:", error.message);
    }
    
    return [];
  } catch (error) {
    console.error("Error getting all operators:", error);
    return [];
  }
};

/**
 * Adds an operator (Direct Method - Admin Only)
 * This function should only be used by the contract owner/admin
 */
export const addOperator = async (fundContract, operatorAddress) => {
  if (!fundContract) throw new Error("Fund contract not initialized");
  
  try {
    // Call the addOperator function directly on the fund contract
    const tx = await fundContract.addOperator(operatorAddress);
    return tx;
  } catch (error) {
    console.error("Error adding operator:", error);
    throw error;
  }
};

/**
 * Removes an operator (Direct Method - Admin Only)
 * This function should only be used by the contract owner/admin
 */
export const removeOperator = async (fundContract, operatorAddress) => {
  if (!fundContract) throw new Error("Fund contract not initialized");
  
  try {
    // Call the removeOperator function directly on the fund contract
    const tx = await fundContract.removeOperator(operatorAddress);
    return tx;
  } catch (error) {
    console.error("Error removing operator:", error);
    throw error;
  }
};

/**
 * Gets all delegators for an account
 */
export const fetchDelegators = async (governanceContract, account) => {
  if (!governanceContract || !account) {
    console.error("Cannot get delegators: Missing governance contract or account");
    return [];
  }
  
  try {
    console.log(`Getting all delegators for account: ${account}`);
    return await getDelegatorsFor(governanceContract, account);
  } catch (error) {
    console.error(`Error getting delegators for ${account}:`, error);
    return [];
  }
};

/**
 * Gets voting power for an account
 */
export const getVotingPower = async (governanceContract, account) => {
  if (!governanceContract || !account) {
    console.error("Cannot get voting power: Missing governance contract or account");
    return "0";
  }
  
  try {
    console.log(`Getting voting power for account: ${account}`);
    const votingPower = await governanceContract.getVotingPower(account);
    return ethers.utils.formatEther(votingPower);
  } catch (error) {
    console.error(`Error getting voting power for ${account}:`, error);
    return "0";
  }
};

/**
 * Calculates approval rate from voting history
 */
export const calculateApprovalRate = (votingHistory) => {
  if (!votingHistory || votingHistory.length === 0) return null;
  
  const votesFor = votingHistory.filter(vote => vote.support).length;
  const approvalRate = Math.round((votesFor / votingHistory.length) * 100);
  
  return approvalRate;
};

/**
 * Checks if a delegator is registered
 */
export const isRegisteredDelegator = async (governanceContract, account) => {
  if (!governanceContract || !account) return false;
  
  try {
    // Try to get the delegator profile
    const profile = await governanceContract.delegatorProfiles(account);
    
    // If name exists, it's a registered delegator
    return profile.name && profile.name !== '';
  } catch (error) {
    console.error(`Error checking if ${account} is a registered delegator:`, error);
    return false;
  }
};

/**
 * Fetches a delegator profile from the contract
 */
export const fetchDelegatorProfileFromContract = async (governanceContract, address) => {
  if (!governanceContract || !address) return null;
  
  try {
    console.log(`Fetching delegator profile for ${address} from contract...`);
    
    const profile = await governanceContract.delegatorProfiles(address);
    
    // Check if the profile exists (has a name)
    if (!profile.name || profile.name === '') {
      console.log(`No profile found for ${address}`);
      return null;
    }
    
    // Get voting history for approval rate calculation
    const votingHistory = await getUserVotingHistory(governanceContract, address);
    const approvalRate = calculateApprovalRate(votingHistory);
    
    return {
      name: profile.name,
      description: profile.description,
      acceptingDelegations: profile.acceptingDelegations,
      votingHistoryCount: votingHistory.length,
      approvalRate
    };
  } catch (error) {
    console.error(`Error fetching delegator profile for ${address}:`, error);
    return null;
  }
};

/**
 * Stores delegation action in local storage for history tracking
 * (For backwards compatibility)
 */
export const storeDelegationHistory = (delegator, delegatee, action) => {
  try {
    // Get existing history
    const historyJSON = localStorage.getItem('delegation_history') || '[]';
    const history = JSON.parse(historyJSON);
    
    // Add new entry
    history.push({
      delegator,
      delegatee,
      action, // 'delegate' or 'undelegate'
      timestamp: Date.now()
    });
    
    // Store back
    localStorage.setItem('delegation_history', JSON.stringify(history));
    
    return true;
  } catch (error) {
    console.error(`Error storing delegation history:`, error);
    return false;
  }
};